import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { CONFIG } from "@/global-config";
import { authStore } from "@/store/useAuthStore";

// ----------------------------------------------------------------------

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _retried?: boolean;
};

const axiosInstance = axios.create({
  baseURL: CONFIG.swaggerUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = authStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 동시 다발적 401 발생 시 refresh 요청을 하나로 합치기 위한 in-flight 프라미스
let refreshInFlight: Promise<string | null> | null = null;

// refresh 토큰 자체가 거부된 경우(만료·블랙리스트·다른 탭이 이미 회전). 네트워크 오류·5xx·타임아웃은 해당하지 않는다.
const isRefreshRejected = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (error.response?.status === 400 || error.response?.status === 401);

// 다른 탭이 먼저 refresh 해 토큰을 회전시켰다면(ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION 으로 이 탭이 쓴
// refresh 는 블랙리스트됨) localStorage 에 새 쌍이 있다. 그걸 받아 쓰고, 없으면 세션이 정말 끝난 것이다.
const adoptRotatedSession = async (
  usedRefresh: string,
): Promise<string | null> => {
  await authStore.persist.rehydrate();
  const { accessToken, refreshToken } = authStore.getState();
  return refreshToken && refreshToken !== usedRefresh ? accessToken : null;
};

/**
 * 새 access 토큰을 돌려준다. 세션이 정말 끝났으면 `null`, 일시적 실패(네트워크 오류·5xx·타임아웃)면 throw 한다.
 * 호출부는 throw 일 때 토큰을 지우지 않는다 — 배포로 gunicorn 이 재시작되는 동안에도 로그인이 유지돼야 한다.
 */
const refreshAccessToken = async (): Promise<string | null> => {
  const refresh = authStore.getState().refreshToken;
  if (!refresh) return null;

  try {
    // 인터셉터를 재진입하지 않도록 raw axios 사용
    const response = await axios.post(
      `${CONFIG.swaggerUrl}/token/refresh/`,
      { refresh },
      { headers: { "Content-Type": "application/json" } },
    );
    const newAccess: string = response.data.access;
    const rotatedRefresh: string | undefined = response.data.refresh;

    // 이 요청이 진행되는 동안 로그아웃 등으로 refreshToken 이 이미 바뀌었다면
    // (예: null 로 지워짐) 응답이 늦게 와도 되살리지 않는다.
    if (authStore.getState().refreshToken !== refresh) return null;

    authStore.getState().setAccessToken(newAccess);
    if (rotatedRefresh) {
      authStore.getState().setRefreshToken(rotatedRefresh);
    }
    return newAccess;
  } catch (error) {
    if (!isRefreshRejected(error)) throw error;
    return adoptRotatedSession(refresh);
  }
};

const refreshOnce = (): Promise<string | null> => {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

const isAuthEndpoint = (url: string | undefined): boolean =>
  !!url && (url.includes("/token/") || url.includes("/logout/"));

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const original = error.config as RetriableRequestConfig | undefined;

    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isAuthEndpoint(original.url)
    ) {
      original._retried = true;

      let newAccess: string | null;
      try {
        newAccess = await refreshOnce();
      } catch {
        // 일시적 실패 — 세션은 살아 있을 수 있으니 토큰은 그대로 두고 원래 요청만 실패시킨다.
        return Promise.reject(error);
      }

      if (newAccess) {
        original.headers.Authorization = `Bearer ${newAccess}`;
        return axiosInstance(original);
      }

      // refresh 실패 → 세션 종료
      authStore.getState().clearAuth();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;

export const axiosFormData = axios.create({
  baseURL: CONFIG.swaggerUrl,
  timeout: 10000,
});

axiosFormData.interceptors.request.use(
  (config) => {
    const token = authStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ----------------------------------------------------------------------

/** 사건 id — 정수. `":id"` 는 MSW 경로 패턴용이다(`socialEvent.event_*(":id")`). */
type SocialEventId = number | ":id";

/**
 * @description endpoint must be ended with `/`
 * @deprecated Do not use this. use orval client instead.
 */
export const endpoints = {
  auth: {
    signIn: "/token/", // 로그인
    refresh: "/token/refresh/",
    me: "/me/",
    logout: "/logout/",
  },
  video: {
    video: "/video/",
    comment: "/video/comment/",
    comment_detail(id: number | string) {
      return `/video/comment/${id}/`;
    },
    tags: "/video/tags/",
  },
  emotion: {
    distribution: "/sentiment/emotion/distribution/", // 감정 분포 (bar chart용)
    // 홈(감정 요약, /home-new) 대시보드 카드 전용
    regionalIntensity: "/sentiment/emotion/regional-intensity/", // 지역별 감정 강도(버블)
    coreSummary: "/sentiment/emotion/core-summary/", // 핵심 요약 지표(전일 기준)
    keywordCloud: "/sentiment/emotion/keyword-cloud/", // 핵심 키워드 클라우드
  },
  sentiment: {
    overall: "/sentiment/overall/", // 전체 감정 점수
    keywords: "/sentiment/keywords/", // 주요 키워드별 감정 데이터
    trend: "/sentiment/trend/", // 감정 추이 그래프 (시계열)
    distribution: "/sentiment/distribution/", // 감정 분포
    insights: "/sentiment/insights/", // AI 해설 & 인사이트
  },
  suicidePrediction: {
    chart: "/suicide-prediction/chart/", // 월별 감정 추이 그래프
    all: "/suicide-prediction/all/", // SEEI 지수 및 변화율 전체 데이터
  },
  socialEvent: {
    events: "/social-events/social-events/", // 사건·사고 목록/생성
    event_detail(id: SocialEventId) {
      return `/social-events/social-events/${id}/`; // 사건·사고 상세/수정/삭제
    },
    // 사건별 파생 데이터(서버 집계). details(JSON) 평탄화·키워드 집계·연관 구조를
    // 프런트가 계산하지 않도록 백엔드가 화면용 형태로 내려주는 서브리소스들.
    event_accident(id: SocialEventId) {
      return `/social-events/social-events/${id}/accident/`; // 사고 상세 뷰(평탄화)
    },
    event_keywords(id: SocialEventId) {
      return `/social-events/social-events/${id}/keywords/`; // 언급 키워드 집계
    },
    event_network(id: SocialEventId) {
      return `/social-events/social-events/${id}/network/`; // 사고 연관 구조 그래프
    },
    event_videos(id: SocialEventId) {
      return `/social-events/social-events/${id}/videos/`; // 사건에 연결된 영상 목록
    },
    event_link_video(id: SocialEventId) {
      return `/social-events/social-events/${id}/link-video/`; // 단일 영상 수동 연결(POST)
    },
    event_unlink_video(id: SocialEventId) {
      return `/social-events/social-events/${id}/unlink-video/`; // 단일 영상 수동 해제(DELETE)
    },
    event_sentiment_trend(id: SocialEventId) {
      return `/social-events/social-events/${id}/sentiment_trend/`; // 사건에 연결된 영상 기준 일자별 감정 추이(7감정)
    },
  },
  // 대시보드 패널 전용 엔드포인트 (현재 MSW 목으로만 제공).
  dashboard: {
    coreEmotions: "/dashboard/core-emotions/", // 핵심 감정 지표
    todaySummary: "/dashboard/today-summary/", // 오늘 요약
    emotionTrend: "/dashboard/emotion-trend/", // 감정 추이 그래프 (백엔드 제공, MSW 폴백)
    suicideRiskSignal: "/dashboard/suicide-risk-signal/", // 오늘의 자살 위험 신호
    stageActionGuide: "/dashboard/stage-action-guide/", // 단계별 행동지침
    privacyEthics: "/dashboard/privacy-ethics/", // 개인정보·윤리·민감도 점검
    regionalEmotion: "/dashboard/regional-emotion/", // 지역별 감정 현황
    emotionRiskRising: "/sentiment/dashboard/emotion-risk-rising/", // 감정 위험 상승 지역 Top5
    realtimeAnomaly: "/dashboard/realtime-anomaly/", // 실시간 이상 탐지 알림
    topIssues: "/dashboard/top-issues/", // 주요 이슈 Top 5
    analysisInsight: "/dashboard/analysis-insight/", // 분석 인사이트
    forecastStage: "/dashboard/forecast-stage/", // 예측 경보 단계 (투트랙)
    emotionNetwork: "/dashboard/emotion-network/", // 감정 연결도 네트워크
    anomalyTop6: "/dashboard/anomaly-top6/", // 이상 징후 탐지 TOP 6 (투트랙)
    forecastTwoWeek: "/dashboard/forecast-two-week/", // 예측 경보 (2주 후 전망)
    issueTimeline: "/dashboard/issue-timeline/", // 이슈 발생 타임라인 (투트랙)
    keywordCluster: "/dashboard/keyword-cluster/", // 관련 키워드 클러스터 (투트랙)
    responseTarget: "/dashboard/response-target/", // 권고 대응·지원 타겟 (투트랙)
    ethicsSensitivity: "/dashboard/ethics-sensitivity/", // 개인정보·윤리·민감도 점검 (민감도 상세)
  },
  // 네이버 뉴스 패널 전용 엔드포인트 (현재 MSW 목으로만 제공).
  // 백엔드 프록시가 서버 측 클라이언트 ID/Secret 으로 네이버 검색 API 를 호출하도록
  // 설계한 경로다. 프런트는 비밀키를 보관하지 않는다.
  naverNews: {
    search: "/naver-news/search/", // 뉴스 검색 결과 (네이버 원본 형태: items[])
    volume: "/naver-news/volume/", // 일자별 보도량 집계
    keywords: "/naver-news/keywords/", // 키워드 빈도 집계
    sources: "/naver-news/sources/", // 언론사별 보도 비중 집계
  },
  // 지역별 현황 모니터링 페이지 카드 전용 엔드포인트 (regionVideos 만 실집계, 나머지는 백엔드 MOCK).
  regionalMonitoring: {
    signals: "/sentiment/regional-monitoring/signals/", // 17개 시·도 부정 정서 비율·순위
    regionDetail: "/sentiment/regional-monitoring/region-detail/", // 선택 지역 상세 (?region=)
    regionVideos: "/sentiment/regional-monitoring/region-videos/", // 지역 채널 인기 영상 (?region=, 실집계)
  },
  // 조기경보 시스템 페이지 카드 전용 엔드포인트 (백엔드 MOCK).
  earlyWarning: {
    nationalAlertStatus: "/sentiment/early-warning/national-alert-status/", // 시·도별 경보 단계
    alertMessageSample: "/sentiment/early-warning/alert-message-sample/", // 알림 메시지 샘플 (MSW mock 핸들러 전용)
  },
  // 데이터 통합 모니터링 페이지 카드 전용 엔드포인트 (백엔드 MOCK).
  dataIntegration: {
    lagCorrelation: "/sentiment/data-integration/lag-correlation/", // 시차 상관 (Lag 1~30일)
    dualTimeSeries: "/sentiment/data-integration/dual-time-series/", // 이중 시계열 그래프
    dataSourceLegend: "/sentiment/data-integration/data-source-legend/", // 데이터 소스 범례
    aiInterpretation: "/sentiment/data-integration/ai-interpretation/", // AI 해석
  },
} as const;
